import {
  LayoutDashboard,
  BarChart3,
  FileText,
  Factory,
  Users,
  Target,
  Shield,
  Database,
  CircleDot,
  Leaf,
  Briefcase,
  FolderKanban,
  UserCheck,
  Link2,
  Bot,
  BookOpen,
  Settings,
  type LucideIcon,
} from "lucide-react";
import type { QueryClient } from "@tanstack/react-query";
import {
  getReportSettings,
  fetchFacilities,
} from "@/features/reports/api/reportService";
import { STALE } from "@/lib/query-constants";

export interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  permission?: string;
  planPermission?: string;
  badge?: "ai";
  prefetch?: (client: QueryClient) => void;
}

export interface NavSection {
  title?: string;
  items: NavItem[];
}

export const mainNavSections: NavSection[] = [
  {
    title: "Overview",
    items: [
      {
        label: "Home",
        path: "/home",
        icon: LayoutDashboard,
        planPermission: "DASHBOARD",
      },
      {
        label: "Dashboard",
        path: "/dashboard",
        icon: BarChart3,
        planPermission: "DASHBOARD",
      },
      {
        label: "Reports",
        path: "/reports",
        icon: FileText,
        permission: "READ_REPORT",
        planPermission: "REPORTS",
        prefetch: (client) => {
          client.prefetchQuery({
            queryKey: ["report-settings"],
            queryFn: () => getReportSettings(true),
            staleTime: STALE.TEN_MIN,
          });
          client.prefetchQuery({
            queryKey: ["report-facilities"],
            queryFn: () => fetchFacilities(),
          });
        },
      },
    ],
  },
  {
    title: "Data Collection",
    items: [
      {
        label: "Emissions",
        path: "/log/emissions",
        icon: Leaf,
        permission: "READ_EMISSIONS",
        planPermission: "EMISSIONS",
      },
      {
        label: "Assets",
        path: "/assets",
        icon: Briefcase,
        permission: "READ_ASSET_LIST",
        planPermission: "ASSETS",
      },
      {
        label: "Suppliers",
        path: "/partners/suppliers/dashboard",
        icon: UserCheck,
        permission: "READ_SUPPLIER_LIST",
        planPermission: "SRM",
      },
      {
        label: "Customers",
        path: "/partners/customers",
        icon: Link2,
        permission: "READ_CUSTOMER_LIST",
      },
      {
        label: "Standard Factors",
        path: "/datasource/standardemissionfactor",
        icon: CircleDot,
        permission: "READ_EMISSION_FACTOR",
        planPermission: "EMISSIONS",
      },
      {
        label: "Custom Factors",
        path: "/datasource/customemissionfactor",
        icon: Database,
        permission: "CREATE_EMISSION_FACTOR",
        planPermission: "CUSTOM_EMISSIONS",
      },
      {
        label: "Offsets",
        path: "/offsets",
        icon: FolderKanban,
        permission: "READ_OFFSET_LIST",
        planPermission: "OFFSETS",
      },
    ],
  },
  {
    title: "Organisation",
    items: [
      {
        label: "Facilities",
        path: "/organisation/facilities",
        icon: Factory,
        permission: "READ_ALL_FACILITIES",
        planPermission: "FACILITY",
      },
      {
        label: "Employees",
        path: "/organisation/employees",
        icon: Users,
        permission: "READ_EMPLOYEE_LIST",
        planPermission: "USER",
      },
    ],
  },
  {
    title: "Strategy & Compliance",
    items: [
      {
        label: "Targets",
        path: "/targets",
        icon: Target,
        permission: "READ_TARGET_LIST",
        planPermission: "TARGETS",
      },
      {
        label: "Disclosures",
        path: "/organisation/disclosures",
        icon: FileText,
        permission: "READ_DISCLOSURE",
        planPermission: "DISCLOSURE",
      },
      {
        label: "Audits",
        path: "/audits",
        icon: Shield,
        permission: "READ_AUDIT_REQUESTS_LIST",
        planPermission: "AUDIT",
      },
    ],
  },
];

export const utilityNavItems: NavItem[] = [
  {
    label: "AI Assistant",
    path: "/chat",
    icon: Bot,
    badge: "ai",
    planPermission: "DASHBOARD",
  },
  { label: "Resource Centre", path: "/resourcecentre", icon: BookOpen },
  {
    label: "Settings",
    path: "/settings",
    icon: Settings,
    permission: "UPDATE_SETTINGS_INFO",
    planPermission: "DASHBOARD",
  },
];
